/**
 * Client mirror of `backend/src/constants/orgPermissions.ts`.
 *
 * Labels and grouping live here because only the settings matrix needs them.
 * The *decisions* do not: the server sends each member the flat list of keys
 * they hold (`Organization.myPermissions`), so `can()` never has to reproduce
 * the ceiling or the revocation logic. Keeping the client ignorant of the matrix
 * is what stops the two copies drifting into disagreement.
 *
 * Client checks are for chrome only — hiding a link the caller cannot use. Every
 * key is enforced again in the resolver that owns the capability.
 */

export const ORG_POLICY_ROLES = ["admin", "supervisor", "agent"] as const;
export type OrgPolicyRole = (typeof ORG_POLICY_ROLES)[number];

/**
 * In the member-role enum but assignable by nothing — see the backend constant
 * for the full accounting. Kept so the labels stay total over the schema enum,
 * and excluded from the matrix, which only offers roles a member can hold.
 */
export const ORG_UNASSIGNABLE_ROLES = ["editor", "viewer"] as const;

export const ORG_MEMBER_ROLES = ["owner", ...ORG_POLICY_ROLES, ...ORG_UNASSIGNABLE_ROLES] as const;
export type OrgMemberRole = (typeof ORG_MEMBER_ROLES)[number];

export const ORG_ROLE_LABELS: Record<OrgMemberRole, string> = {
    owner: "Owner",
    admin: "Admins",
    supervisor: "Supervisors",
    agent: "Agents",
    editor: "Editors",
    viewer: "Viewers",
};

export const ORG_PERMISSION_KEYS = [
    "viewBilling",
    "manageBilling",
    "manageOrganizationSettings",
    "viewApiKeys",
    "manageApiKeys",
    "viewTeam",
    "manageTeam",
    "viewDataCollections",
    "manageDataCollections",
    "deleteDataCollections",
    "viewCallCentreFloor",
    "viewCallCentreUsage",
    "manageCallCentreRouting",
    "manageCallCentreFollowUps",
    "viewAllLeads",
    "viewDeals",
    "manageDeals",
    "manageSequences",
    "viewUnifiedInbox",
    "useAgentAssist",
] as const;

export type OrgPermissionKey = (typeof ORG_PERMISSION_KEYS)[number];

export const ORG_PERMISSION_LABELS: Record<OrgPermissionKey, string> = {
    viewBilling: "View the plan and payment methods",
    manageBilling: "Change plan and payment methods",
    manageOrganizationSettings: "Change organization settings",
    viewApiKeys: "View API keys",
    manageApiKeys: "Create and revoke API keys",
    viewTeam: "View team members",
    manageTeam: "Invite and deactivate team members",
    viewDataCollections: "View data collections",
    manageDataCollections: "Create and edit data collections",
    deleteDataCollections: "Delete data collections",
    viewCallCentreFloor: "See every rep's calls on the floor",
    viewCallCentreUsage: "View call centre minutes and charges",
    manageCallCentreRouting: "Change ring groups, menus and numbers",
    manageCallCentreFollowUps: "Change automated follow-up rules",
    viewAllLeads: "See the whole lead book, not just their own",
    viewDeals: "View the deal pipeline",
    manageDeals: "Create deals and move them between stages",
    manageSequences: "Enrol contacts in outbound sequences",
    viewUnifiedInbox: "Use the cross-channel inbox",
    useAgentAssist: "Generate AI reply drafts in the composer",
};

export const ORG_PERMISSION_HINTS: Partial<Record<OrgPermissionKey, string>> = {
    viewCallCentreFloor:
        "The wallboard shows every rep's live calls, which is what makes it a supervision tool.",
    viewAllLeads: "Without this, a member only ever sees leads assigned to them plus the open pool.",
    viewCallCentreUsage: "Minutes and spend, separate from the subscription itself.",
    manageTeam: "Covers inviting admins, agents and supervisors, and deactivating them.",
    viewBilling:
        "Every role can read the plan today — the sidebar hides it from agents and supervisors, but the route does not. Revoke to close that gap.",
    manageSequences:
        "An enrolment places real calls and messages against the org's consent and quiet-hours rules, so it spends money and reputation.",
    viewUnifiedInbox:
        "The one inbox spans every channel and every contact. Revoke to keep a member in the per-channel queues they are assigned to.",
    manageDeals: "Covers closing a deal as won or lost, which is what the pipeline forecast is built on.",
    useAgentAssist:
        "Each draft spends model tokens on the org's bill. Revoking leaves saved replies and the knowledge base, which cost nothing.",
};

export const ORG_PERMISSION_GROUPS: { label: string; keys: readonly OrgPermissionKey[] }[] = [
    { label: "Billing", keys: ["viewBilling", "manageBilling"] },
    {
        label: "Organization",
        keys: ["manageOrganizationSettings", "viewApiKeys", "manageApiKeys", "viewTeam", "manageTeam"],
    },
    { label: "Data", keys: ["viewDataCollections", "manageDataCollections", "deleteDataCollections"] },
    {
        label: "Call centre",
        keys: [
            "viewCallCentreFloor",
            "viewCallCentreUsage",
            "viewAllLeads",
            "manageCallCentreRouting",
            "manageCallCentreFollowUps",
        ],
    },
    { label: "Sales", keys: ["viewDeals", "manageDeals", "manageSequences"] },
    { label: "Support", keys: ["viewUnifiedInbox", "useAgentAssist"] },
];

export type OrgPermissionChecker = (key: OrgPermissionKey) => boolean;

/**
 * Build a `can()` from the key list the server sent.
 *
 * A member with no organization has no capabilities, but neither does one whose
 * `me` payload predates this field — so an absent list would silently hide every
 * gated entry from everyone mid-deploy. `null`/`undefined` therefore means "the
 * server did not say", and falls open; only an explicit empty array means "you
 * hold nothing". Safe because these gates are chrome, not enforcement.
 */
export const buildOrgPermissionChecker = (
    granted: readonly string[] | null | undefined,
): OrgPermissionChecker => {
    if (granted == null) return () => true;
    const held = new Set(granted);
    return (key) => held.has(key);
};
